import { Alert, Skeleton, Typography } from "@mui/material";
import { GeneratorStatusExceedanceDetailQuery } from "generated-graphql/graphql";

import { DrawerSectionHeading } from "./DrawerSection";
import { ExceedanceForDrawer } from "./ExceedanceActionDrawer";
import { formatPeriodMonth } from "./periodFormat";
import { StatusComparison } from "./StatusComparison";

type Detail = GeneratorStatusExceedanceDetailQuery["generatorStatusExceedanceDetail"];

export function FindingStatusComparison({
  exceedance,
  detail,
  loading,
}: {
  exceedance: ExceedanceForDrawer;
  detail?: Detail;
  loading: boolean;
}) {
  if (exceedance.mismatchMonths === 0) {
    return (
      <HistoricalComparison
        state={exceedance.state}
        resolution={detail?.resolution}
        loading={loading}
      />
    );
  }
  const latest = exceedance.lastPeriodStart ?? exceedance.periodStart;
  const stateMonth = exceedance.stateComparisonPeriodStart;
  return (
    <>
      <DrawerSectionHeading>Status discrepancy</DrawerSectionHeading>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {formatPeriodMonth(latest)} · Latest affected month.
      </Typography>
      <StatusComparison
        values={{
          ...exceedance,
          calculationAvailable: detail?.calculatedMonths.some(
            (month) => month.periodStart === latest
          ),
        }}
        stateMonth={stateMonth && stateMonth !== latest ? stateMonth : undefined}
      />
    </>
  );
}

function HistoricalComparison({
  state,
  resolution,
  loading,
}: {
  state: string | null;
  resolution: Detail["resolution"] | undefined;
  loading: boolean;
}) {
  return (
    <>
      <DrawerSectionHeading>Previously recorded comparison</DrawerSectionHeading>
      {resolution ? (
        <>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            No affected months remain in the current data. The comparison below is preserved from
            the prior review.
          </Alert>
          <StatusComparison
            values={{
              state,
              reportedFederalStatus: resolution.reportedFederalStatusSnapshot,
              calculatedFederalStatus: resolution.calculatedFederalStatusSnapshot,
              reportedStateStatus: resolution.reportedStateStatusSnapshot,
              calculatedStateStatus: resolution.calculatedStateStatusSnapshot,
              direction: resolution.direction,
              stateDirection: resolution.stateDirection,
              calculationAvailable: Boolean(resolution.direction || resolution.stateDirection),
            }}
          />
        </>
      ) : loading ? (
        <Skeleton variant="rounded" height={120} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          The previously recorded comparison is unavailable.
        </Typography>
      )}
    </>
  );
}
